import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { collection, getDocs, getFirestore } from '@firebase/firestore'
import { app } from '../../firebase.js'
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import Table from 'react-bootstrap/Table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsRotate, faChartPie, faMagnifyingGlass, faUsers } from '@fortawesome/free-solid-svg-icons'
import LeftBar from '../../Components/LeftBar'
import Dashnav from '../../Components/Dashnav'
import '../../Pages/dash.css'

const countFormatter = new Intl.NumberFormat('en-KE')

const formatCount = (value) => countFormatter.format(Number(value || 0))

const formatPercent = (value) => {
  const safeValue = Number.isFinite(value) ? value : 0
  return `${safeValue.toFixed(1)}%`
}

const normalizeText = (value) => String(value || '').trim()

const SOURCE_COLORS = [
  '#0f766e',
  '#1d4ed8',
  '#d97706',
  '#db2777',
  '#7c3aed',
  '#059669',
  '#b45309',
  '#475569'
]

const SourceTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  const row = payload[0]?.payload || {}

  return (
    <div className="sourcesOfStudentsTooltip">
      <strong>{row.name}</strong>
      <div className="sourcesOfStudentsTooltipRow">
        <span>Students</span>
        <strong>{formatCount(row.value)}</strong>
      </div>
      <div className="sourcesOfStudentsTooltipRow">
        <span>Share</span>
        <strong>{formatPercent(row.share)}</strong>
      </div>
    </div>
  )
}

function StudentSources() {
  const db = getFirestore(app)
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const fetchData = useCallback(async (options = {}) => {
    const initialLoad = options.initial === true

    if (initialLoad) {
      setLoading(true)
    } else {
      setRefreshing(true)
    }

    setError('')

    try {
      const querySnapshot = await getDocs(collection(db, 'users'))
      const items = []

      querySnapshot.forEach((docItem) => {
        const data = docItem.data()
        const createdAt = data.appDate?.toDate ? data.appDate.toDate() : null

        items.push({
          name: normalizeText(data.hear) || 'Unknown',
          recordDateMs: createdAt ? createdAt.getTime() : null
        })
      })

      setRecords(items)
    } catch (fetchError) {
      console.error('Failed to load student sources:', fetchError)
      setRecords([])
      setError('We could not load the student sources right now. Please try again.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [db])

  useEffect(() => {
    fetchData({ initial: true })
  }, [fetchData])

  const filteredRecords = useMemo(() => {
    const search = searchTerm.trim().toLowerCase()
    const startMs = startDate ? new Date(`${startDate}T00:00:00.000`).getTime() : null
    const endMs = endDate ? new Date(`${endDate}T23:59:59.999`).getTime() : null

    return records.filter((record) => {
      if (search && !record.name.toLowerCase().includes(search)) {
        return false
      }

      if ((startMs !== null || endMs !== null) && record.recordDateMs === null) {
        return false
      }

      if (startMs !== null && record.recordDateMs !== null && record.recordDateMs < startMs) {
        return false
      }

      if (endMs !== null && record.recordDateMs !== null && record.recordDateMs > endMs) {
        return false
      }

      return true
    })
  }, [records, searchTerm, startDate, endDate])

  const sourceRows = useMemo(() => {
    const counts = new Map()

    filteredRecords.forEach((record) => {
      counts.set(record.name, (counts.get(record.name) || 0) + 1)
    })

    return Array.from(counts.entries())
      .map(([name, value]) => ({
        name,
        value
      }))
      .sort((first, second) => {
        if (second.value !== first.value) {
          return second.value - first.value
        }

        return first.name.localeCompare(second.name)
      })
      .map((row) => ({
        ...row,
        share: filteredRecords.length > 0 ? (row.value / filteredRecords.length) * 100 : 0
      }))
  }, [filteredRecords])

  const totalStudents = filteredRecords.length
  const visibleSources = sourceRows.length
  const topSource = sourceRows[0] || null
  const topSources = sourceRows.slice(0, 5)
  const chartData = sourceRows.slice(0, 8)
  const hasRows = sourceRows.length > 0
  const hasChartData = chartData.length > 0
  const emptyMessage = records.length
    ? 'No sources match the current filters.'
    : 'No student source records were found yet.'

  const chartSummary = chartData.length
    ? `${chartData.slice(0, 5).map((row) => row.name).join(', ')}${chartData.length > 5 ? ' + others' : ''}`
    : 'No series available'

  return (
    <div className="mainContainer sourcesOfStudentsPage">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />

        <div className="sourcesOfStudentsContent">
          <div className="appsHeader">
            <div className="attendanceHeaderCopy">
              <p className="appsKicker">Data Analytics</p>
              <h4>Sources of Students</h4>
              <p className="appsSubtext">
                See which intake sources contribute the most students and compare how the mix changes over a chosen date range.
              </p>
            </div>

            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>Total students</span>
                <strong>{formatCount(totalStudents)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Sources visible</span>
                <strong>{formatCount(visibleSources)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Top source share</span>
                <strong>{topSource ? formatPercent(topSource.share) : '0.0%'}</strong>
              </div>
              <div className="appsStatChip">
                <span>Leading source</span>
                <strong>{topSource ? topSource.name : '—'}</strong>
              </div>
            </div>
          </div>

          <div className="appsFiltersCard sourcesOfStudentsFiltersCard">
            <div className="appsSearch input-group sourcesOfStudentsSearch">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </span>
              <Form.Control
                type="text"
                placeholder="Search source..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>

            <div className="sourcesOfStudentsDateGroup">
              <div className="sourcesOfStudentsDateControl">
                <span>From</span>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                />
              </div>
              <div className="sourcesOfStudentsDateControl">
                <span>To</span>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              </div>
            </div>

            <div className="sourcesOfStudentsFiltersMeta">
              <span>{formatCount(totalStudents)} records visible</span>
              <span>{formatCount(chartData.length)} sources shown in chart</span>
            </div>

            <Button variant="outline-dark" onClick={() => fetchData()} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          {error && (
            <div className="incomeStateCard mb-3">
              <p>{error}</p>
              <Button variant="outline-dark" onClick={() => fetchData()} disabled={refreshing}>
                <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
                Try again
              </Button>
            </div>
          )}

          <div className="sourcesOfStudentsSummaryGrid">
            <div className="sourcesOfStudentsSummaryCard">
              <span>Leading source</span>
              <strong>{topSource ? topSource.name : '—'}</strong>
              <p>{topSource ? `${formatCount(topSource.value)} students tracked` : 'No data yet.'}</p>
            </div>
            <div className="sourcesOfStudentsSummaryCard">
              <span>Top source share</span>
              <strong>{topSource ? formatPercent(topSource.share) : '0.0%'}</strong>
              <p>{topSource ? 'Share of the currently visible records.' : 'No data yet.'}</p>
            </div>
            <div className="sourcesOfStudentsSummaryCard">
              <span>Visible sources</span>
              <strong>{formatCount(visibleSources)}</strong>
              <p>Unique sources in the filtered window.</p>
            </div>
            <div className="sourcesOfStudentsSummaryCard">
              <span>Filtered records</span>
              <strong>{formatCount(totalStudents)}</strong>
              <p>Students matching the current search and date range.</p>
            </div>
          </div>

          <div className="appsTableCard sourcesOfStudentsChartCard">
            <div className="appsTableHeader">
              <h6>
                <FontAwesomeIcon icon={faChartPie} className="me-2" />
                Student source mix
              </h6>
              <span>{chartSummary}</span>
            </div>

            <div className="sourcesOfStudentsChartWrap">
              {loading ? (
                <div className="incomeStateCard">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading student sources...</span>
                  </Spinner>
                  <p>Loading student sources...</p>
                </div>
              ) : hasChartData ? (
                <div className="sourcesOfStudentsChartBox">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={150}
                        innerRadius={88}
                        paddingAngle={3}
                        stroke="#ffffff"
                        strokeWidth={2}
                        labelLine={false}
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${entry.name}`} fill={SOURCE_COLORS[index % SOURCE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<SourceTooltip />} />
                      <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px', color: '#64748b' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="incomeStateCard">
                  <p>{emptyMessage}</p>
                </div>
              )}
            </div>
          </div>

          <div className="appsInsightsCard sourcesOfStudentsInsightsCard">
            <div className="appsInsightsHeader">
              <h6>
                <FontAwesomeIcon icon={faUsers} className="me-2" />
                Source highlights
              </h6>
              <span>{formatCount(sourceRows.length)} sources tracked</span>
            </div>

            <div className="sourcesOfStudentsHighlightsGrid">
              <div className="sourcesOfStudentsMiniStats">
                <div className="sourcesOfStudentsMiniStat">
                  <span>Top source</span>
                  <strong>{topSource ? topSource.name : '—'}</strong>
                  <small>{topSource ? formatCount(topSource.value) : 'No data'}</small>
                </div>
                <div className="sourcesOfStudentsMiniStat">
                  <span>Largest slice</span>
                  <strong>{topSource ? formatPercent(topSource.share) : '0.0%'}</strong>
                  <small>Of visible records</small>
                </div>
              </div>

              <div className="appsInsightsList">
                {topSources.length ? (
                  topSources.map((row) => (
                    <div className="appsInsightsItem" key={row.name}>
                      <div>
                        <strong>{row.name}</strong>
                        <span>{formatCount(row.value)} students</span>
                      </div>
                      <small>{formatPercent(row.share)} share</small>
                    </div>
                  ))
                ) : (
                  <div className="incomeStateCard">
                    <p>No source highlights match the current data.</p>
                  </div>
                )}
              </div>
            </div>

            <p className="appsInsightsMore">
              {topSource
                ? `Top source now is ${topSource.name} with ${formatCount(topSource.value)} students.`
                : 'Refresh after new records are added to update the highlights.'}
            </p>
          </div>

          <div className="appsTableCard sourcesOfStudentsTableCard">
            <div className="appsTableHeader">
              <h6>
                <FontAwesomeIcon icon={faUsers} className="me-2" />
                Ranked sources
              </h6>
              <span>{formatCount(sourceRows.length)} results</span>
            </div>

            <div className="appsTableWrap">
              {loading ? (
                <div className="incomeStateCard">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading ranked sources...</span>
                  </Spinner>
                  <p>Loading ranked sources...</p>
                </div>
              ) : (
                <Table hover responsive className="appsTable sourcesOfStudentsTable">
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Students</th>
                      <th>Share</th>
                      <th>Mix</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hasRows ? (
                      sourceRows.map((row) => {
                        const topTotal = sourceRows[0]?.value || 0
                        const progress = topTotal > 0 ? (row.value / topTotal) * 100 : 0

                        return (
                          <tr key={row.name}>
                            <td>
                              <div className="sourcesOfStudentsRowLabel">
                                <strong>{row.name}</strong>
                                <span>{row.value > 0 ? 'Tracked source' : 'No records'}</span>
                              </div>
                            </td>
                            <td>
                              <span className="sourcesOfStudentsTotalCell">{formatCount(row.value)}</span>
                            </td>
                            <td>{formatPercent(row.share)}</td>
                            <td>
                              <div className="sourcesOfStudentsTrack" aria-hidden="true">
                                <span
                                  className="sourcesOfStudentsTrackFill"
                                  style={{ width: `${Math.min(progress, 100)}%` }}
                                />
                              </div>
                            </td>
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan="4" className="appsEmptyState">
                          {emptyMessage}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StudentSources
